import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Chip, IconButton, Tooltip,
  Stack, Pagination, Select, MenuItem,
} from '@mui/material';
import {
  OpenInNew as OpenIcon, Phone as PhoneIcon, Edit as EditIcon,
} from '@mui/icons-material';
import { STATUS_CONFIG, PRIORITY_COLORS, TIMEZONE_LABELS } from './SalesDashboard';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—';
const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : '—';

export default function LeadsListView({ leads, total, page, onPageChange, onOpenDetail, onEditLead, onStatusChange }) {
  const pages = Math.ceil(total / 100);

  return (
    <Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Business', 'Owner', 'Phone', 'Status', 'Priority', 'Timezone', 'Revenue', 'Requested', 'Last Contact', 'Notes', 'Actions']
                .map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.map((lead) => {
              const phone = lead.mobilePhone || lead.phone || lead.businessPhone;
              const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new_lead;
              return (
                <TableRow
                  key={lead._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOpenDetail(lead)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }} title={lead.businessName}>
                      {lead.businessName || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{lead.industry || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{lead.email || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    {phone
                      ? <Tooltip title={`Call ${phone}`}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={`tel:${phone}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ color: 'success.main', textDecoration: 'none', whiteSpace: 'nowrap' }}
                          >
                            {phone}
                          </Typography>
                        </Tooltip>
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={lead.status}
                      size="small"
                      variant="standard"
                      disableUnderline
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); onStatusChange(lead._id, e.target.value); }}
                      sx={{
                        '& .MuiSelect-select': {
                          py: 0.25,
                          px: 0.75,
                          bgcolor: cfg.bg,
                          color: cfg.color,
                          borderRadius: 1,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                        }
                      }}
                    >
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <MenuItem key={k} value={k} sx={{ fontSize: '0.8rem' }}>{v.label}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.priority}
                      size="small"
                      sx={{
                        bgcolor: PRIORITY_COLORS[lead.priority],
                        color: '#fff',
                        textTransform: 'capitalize',
                        fontSize: '0.65rem',
                        height: 18,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>
                      {TIMEZONE_LABELS[lead.timezone]?.split('(')[0] || lead.timezone}
                    </Typography>
                  </TableCell>
                  <TableCell>{currency(lead.monthlyRevenue)}</TableCell>
                  <TableCell>{currency(lead.requestedAmount)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>{fmtDate(lead.lastContactedAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={lead.notes?.length || 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" gap={0.25}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => onEditLead(lead)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Details"><IconButton size="small" onClick={() => onOpenDetail(lead)}><OpenIcon fontSize="small" /></IconButton></Tooltip>
                      {phone && (
                        <Tooltip title={`Call ${phone}`}>
                          <IconButton size="small" component="a" href={`tel:${phone}`}><PhoneIcon fontSize="small" sx={{ color: 'success.main' }} /></IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={pages} page={page} onChange={(_, p) => onPageChange(p)} color="primary" />
        </Box>
      )}
    </Box>
  );
}
